import { webEditorTooltipPosition } from '../../ui.js';

const TOOLTIP_ID = 'vp-editor-global-tooltip';
const TOOLTIP_TARGET_SELECTOR = '[data-editor-tooltip], [title], button[aria-label], a[aria-label], label[aria-label], input[aria-label]';

interface TooltipController {
  activeTarget: HTMLElement | null;
  previousDescribedBy: string | null;
  timeout: number | null;
  tooltip: HTMLDivElement;
}

let mounted: { root: HTMLElement; observer: MutationObserver; abort: AbortController; controller: TooltipController } | null = null;

const unmountTooltipRoot = () => {
  if (!mounted) return;
  hideTooltip(mounted.controller);
  mounted.observer.disconnect();
  mounted.abort.abort();
  mounted.controller.tooltip.remove();
  mounted = null;
};

const tooltipTarget = (origin: EventTarget | null) =>
  origin instanceof Element
    ? origin.closest<HTMLElement>(TOOLTIP_TARGET_SELECTOR)
    : null;

const upgradeTooltipTarget = (target: HTMLElement) => {
  const nativeTitle = target.getAttribute('title');
  if (nativeTitle && !target.dataset.editorTooltip) target.dataset.editorTooltip = nativeTitle;
  if (!target.dataset.editorTooltip && target.matches('button, a, label, input')) {
    target.dataset.editorTooltip = target.getAttribute('aria-label') ?? '';
  }
  if (nativeTitle) target.removeAttribute('title');
  const label = target.dataset.editorTooltip?.trim();
  if (label && !target.getAttribute('aria-label') && !target.textContent?.trim()) target.setAttribute('aria-label', label);
  return label ?? '';
};

const clearTimer = (controller: TooltipController) => {
  if (controller.timeout === null) return;
  window.clearTimeout(controller.timeout);
  controller.timeout = null;
};

const hideTooltip = (controller: TooltipController) => {
  clearTimer(controller);
  if (controller.activeTarget) {
    if (controller.previousDescribedBy) controller.activeTarget.setAttribute('aria-describedby', controller.previousDescribedBy);
    else controller.activeTarget.removeAttribute('aria-describedby');
  }
  controller.activeTarget = null;
  controller.previousDescribedBy = null;
  controller.tooltip.hidden = true;
  controller.tooltip.classList.remove('is-visible');
};

const paintTooltip = (controller: TooltipController, target: HTMLElement) => {
  if (!target.isConnected || controller.activeTarget !== target) return;
  const label = upgradeTooltipTarget(target);
  if (!label) return;
  const labelNode = document.createElement('span');
  labelNode.textContent = label;
  controller.tooltip.replaceChildren(labelNode);
  const shortcut = target.dataset.editorShortcut?.trim();
  if (shortcut) {
    const shortcutNode = document.createElement('kbd');
    shortcutNode.textContent = shortcut;
    controller.tooltip.append(shortcutNode);
  }
  controller.tooltip.hidden = false;
  const tooltipRect = controller.tooltip.getBoundingClientRect();
  const position = webEditorTooltipPosition({
    target: target.getBoundingClientRect(),
    tooltipWidth: tooltipRect.width,
    tooltipHeight: tooltipRect.height,
    viewportWidth: window.innerWidth,
    viewportHeight: window.innerHeight,
  });
  controller.tooltip.style.left = `${position.left}px`;
  controller.tooltip.style.top = `${position.top}px`;
  controller.tooltip.dataset.placement = position.placement;
  controller.tooltip.classList.add('is-visible');
  controller.previousDescribedBy = target.getAttribute('aria-describedby');
  target.setAttribute('aria-describedby', TOOLTIP_ID);
};

const scheduleTooltip = (controller: TooltipController, target: HTMLElement, delay: number) => {
  if (!upgradeTooltipTarget(target)) return;
  if (controller.activeTarget !== target) hideTooltip(controller);
  controller.activeTarget = target;
  clearTimer(controller);
  controller.timeout = window.setTimeout(() => {
    controller.timeout = null;
    paintTooltip(controller, target);
  }, delay);
};

export const mountEditorTooltipRoot = (element?: Element) => {
  if (!(element instanceof HTMLElement)) {
    unmountTooltipRoot();
    return;
  }
  if (mounted?.root === element) return;
  unmountTooltipRoot();
  const tooltip = document.createElement('div');
  tooltip.id = TOOLTIP_ID;
  tooltip.className = 'vp-editor-tooltip';
  tooltip.setAttribute('role', 'tooltip');
  tooltip.hidden = true;
  document.body.append(tooltip);
  const controller: TooltipController = { activeTarget: null, previousDescribedBy: null, timeout: null, tooltip };
  const abort = new AbortController();
  const listener = { signal: abort.signal };

  element.querySelectorAll<HTMLElement>(TOOLTIP_TARGET_SELECTOR).forEach(upgradeTooltipTarget);
  const observer = new MutationObserver(records => {
    for (const record of records) {
      if (record.target instanceof HTMLElement && record.type === 'attributes') upgradeTooltipTarget(record.target);
      for (const node of record.addedNodes) {
        if (!(node instanceof HTMLElement)) continue;
        if (node.matches(TOOLTIP_TARGET_SELECTOR)) upgradeTooltipTarget(node);
        node.querySelectorAll<HTMLElement>(TOOLTIP_TARGET_SELECTOR).forEach(upgradeTooltipTarget);
      }
    }
  });
  observer.observe(element, { childList: true, subtree: true, attributes: true, attributeFilter: ['title', 'aria-label'] });

  element.addEventListener('pointerover', event => {
    const target = tooltipTarget(event.target);
    if (target) scheduleTooltip(controller, target, 400);
  }, listener);
  element.addEventListener('pointerout', event => {
    if (controller.activeTarget?.contains(event.relatedTarget as Node | null)) return;
    hideTooltip(controller);
  }, listener);
  element.addEventListener('focusin', event => {
    const target = tooltipTarget(event.target);
    if (target) scheduleTooltip(controller, target, 0);
  }, listener);
  element.addEventListener('focusout', event => {
    if (controller.activeTarget?.contains(event.relatedTarget as Node | null)) return;
    hideTooltip(controller);
  }, listener);
  element.addEventListener('keydown', event => {
    if (event.key === 'Escape') hideTooltip(controller);
  }, listener);
  element.addEventListener('click', () => hideTooltip(controller), listener);
  window.addEventListener('scroll', () => hideTooltip(controller), { capture: true, signal: abort.signal });
  mounted = { root: element, observer, abort, controller };
};
